import type { useGT } from 'gt-react';
import type { OverrideWindowGrain } from './overrideWindow';

export type { OverrideWindowGrain };

// How a procurement override row reads on the lead-time and inventory-policy
// history lists, which is the same on both because both are stored as the same
// kind of history: a row chain where an edit revokes the old row and creates
// its replacement. Only the headline (the pinned days, or the fields a policy
// override sets) belongs to one domain or the other.
// See docs/procurement/lead-time-overrides.md.

type Translate = ReturnType<typeof useGT>;

/** The one column this needs: whichever row a replacement points back at. */
interface SupersedingRow {
  supersedesOverrideId: string | null;
}

/** The window an override applies over, as the row DTOs carry it. */
interface WindowedRow {
  startsAt: string;
  endsAt: string | null;
  reviewAt: string | null;
}

interface AttributedRow extends WindowedRow {
  createdByUserName: string | null;
  reason: string | null;
}

interface DeactivatedRow {
  deactivatedAt: string | null;
  deactivatedByUserName: string | null;
}

// An edit revokes the old row and creates its replacement pointing back at it,
// so the ids a newer row supersedes are the ones that read as "replaced"
// rather than as a deliberate revoke.
export function replacedOverrideIds(
  overrides: readonly SupersedingRow[],
): ReadonlySet<string> {
  return new Set(
    overrides
      .map((o) => o.supersedesOverrideId)
      .filter((id): id is string => id !== null),
  );
}

/**
 * `endsAt` is EXCLUSIVE: it names the first instant after the window, so
 * printing it raw overstates the span by one unit. Stepping back a millisecond
 * lands on the last instant actually covered, which is the bound worth showing
 * and works whatever the grain, including for a legacy row whose bounds never
 * sat on a boundary.
 */
function lastCoveredInstant(endsAt: string): Date {
  return new Date(new Date(endsAt).getTime() - 1);
}

/**
 * Month grain formats in UTC deliberately. A stored `2030-11-01T00:00Z` is
 * October 31 in any negative-offset timezone, so formatting it locally would
 * name the wrong month for every buyer west of UTC.
 */
function formatBound(d: Date, grain: OverrideWindowGrain): string {
  return grain === 'month'
    ? d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        timeZone: 'UTC',
      })
    : d.toLocaleDateString();
}

/** The span the override covers, and when it comes up for review. */
export function overrideWindowLabel(
  o: WindowedRow,
  gt: Translate,
  grain: OverrideWindowGrain = 'day',
): string {
  const from = formatBound(new Date(o.startsAt), grain);
  const span = o.endsAt
    ? `${from} – ${formatBound(lastCoveredInstant(o.endsAt), grain)}`
    : gt('from {date} (open-ended)', {
        context: 'procurement override window label',
        date: from,
      });
  // The review date is advisory rather than a window bound, so it stays a day
  // even on a month-grain override.
  const review = o.reviewAt
    ? gt('review by {date}', {
        context: 'procurement override review date',
        date: new Date(o.reviewAt).toLocaleDateString(),
      })
    : null;
  return [span, review].filter((part) => part !== null).join(' · ');
}

/** The window, then whoever set it, then why: the row's secondary line. */
export function overrideMetaLine(
  o: AttributedRow,
  gt: Translate,
  grain: OverrideWindowGrain = 'day',
): string {
  return [overrideWindowLabel(o, gt, grain), o.createdByUserName, o.reason]
    .filter((part) => !!part)
    .join(' · ');
}

/** Who took the row out of service, and whether that was an edit or a revoke. */
export function overrideDeactivationLine(
  o: DeactivatedRow,
  replaced: boolean,
  gt: Translate,
): string | null {
  if (!o.deactivatedAt || !o.deactivatedByUserName) return null;
  return gt('{action} by {who} on {date}', {
    context: 'procurement override revoke line',
    action: replaced
      ? gt('Replaced', { context: 'procurement override revoke verb' })
      : gt('Revoked', { context: 'procurement override revoke verb' }),
    who: o.deactivatedByUserName,
    date: new Date(o.deactivatedAt).toLocaleDateString(),
  });
}
